/**
 * paths 모듈 factory.
 *
 * 모든 라우트는 함수로 정의되며 type-safe params + query를 받는다.
 * - 파라미터 없는 라우트: `paths.intro()` 또는 `paths.intro({ query: { ref: 'home' } })`
 * - 파라미터 있는 라우트: `paths.shop({ snsTitle: 'abc' })` 또는 `paths.shop({ snsTitle: 'abc', query: { direct: true } })`
 *
 * query는 자동으로 URLSearchParams로 인코딩되며 undefined/null 값은 무시된다.
 */
export type QueryValue = string | number | boolean | undefined | null;
export type Query = Record<string, QueryValue>;

export interface PathOptions {
  query?: Query;
}

/** 파라미터 없는 라우트의 sentinel 타입. keyof = never라 PathBuilder가 옵션-온리 시그니처로 좁힘. */
export type EmptyParams = Record<never, never>;

export type PathBuilder<P = EmptyParams> = keyof P extends never
  ? (options?: PathOptions) => string
  : (params: P & PathOptions) => string;

/** 파라미터 없는 라우트 정의. */
export function defineRoute(template: () => string): PathBuilder<EmptyParams>;
/** 파라미터 있는 라우트 정의. params 객체로 받음. */
export function defineRoute<P extends Record<string, string | number>>(
  template: (params: P) => string,
): PathBuilder<P>;
export function defineRoute(
  template: (...args: unknown[]) => string,
): (input?: Record<string, unknown>) => string {
  return (input: Record<string, unknown> = {}) => {
    const path = template(input);
    const query = (input as PathOptions).query;
    if (!query) return path;
    const entries = Object.entries(query).filter(
      ([, v]) => v !== undefined && v !== null && v !== '',
    );
    if (entries.length === 0) return path;
    const search = new URLSearchParams(
      entries.map(([k, v]) => [k, String(v)] as [string, string]),
    ).toString();
    return search ? `${path}?${search}` : path;
  };
}
